<script setup lang="ts">
  import { onMounted, onUnmounted, provide, reactive, ref, computed } from 'vue';
  import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
  // Icons
  import fileTexMultiple from '@/shared/assets/icons/FileTextMultiple.svg';
  import tableIcon from '@/shared/assets/icons/TableIcon.svg';
  import ExportExcelIcon from '@/shared/assets/icons/ExportExcel.svg';
  // Components
  import {
    TabsMenu,
    FieldSearchBar,
    SearchToolbar,
    ColoredButton,
    SelectInput,
    NoDocuments,
    PaginateOrScroll,
  } from '@/shared/ui';
  import { SEARCH_TYPE_FIELD_OPTIONS } from '@/shared/lib';
  import { HistoryTable } from '../ui';
  // Constants
  import { HISTORY_ORDER_OPTIONS, HISTORY_DEFAULT_ORDER_OPTION } from '@/shared/config';
  import {
    VALIDITY_HISTORY_DEFAULT_PAGE_SIZE,
    buildDefaultValidityHistoryFilters,
    getValidityHistorySearchUrl,
    getValidityHistoryTabConfig,
  } from '../config';
  // Composables
  import { useFilterStore } from '@/entities/Filter';
  import { useTableFilterModal } from '@/shared/lib/use';
  import { SortOrderInjectionKey } from '@/shared/lib/sortOrder';
  // Types
  import FilterHistoryModal from '../ui/FilterHistoryModal.vue';
  import {
    useDocumentRevisionSearch,
    useDocumentRevisionsStore,
    type ValidityHistoryItem,
  } from '@/entities/Document';
  import {
    useDocumentSearchTypeStore,
    DocumentType,
    type DocumentRegistrationTabOptionId,
    documentTypeToTabOptionId,
    isDocumentRegistrationTabOptionId,
    tabOptionIdToDocumentType,
    type SearchFilter,
  } from '@/shared/lib';
  import { useDocumentSelection } from '@/features/Document';
  import { useValidityHistoryExport } from '../lib/useValidityHistoryExport';

  const route = useRoute();
  const router = useRouter();
  const documentStore = useDocumentRevisionsStore();
  const { resetSelection } = useDocumentSelection();

  const OPTIONS = [
    { id: 'documentos', text: 'Documentos', icon: fileTexMultiple },
    { id: 'registros', text: 'Registros', icon: tableIcon },
  ] satisfies Array<{ id: DocumentRegistrationTabOptionId; text: string; icon: string }>;

  const urlDocumentType = route.query.documentType as string;
  const initialDocumentType =
    urlDocumentType === DocumentType.REGISTRATION
      ? DocumentType.REGISTRATION
      : DocumentType.DOCUMENT;

  const { searchType } = useDocumentSearchTypeStore();
  const selectedDocumentType = ref<DocumentType>(initialDocumentType);
  const tabConfig = computed(() => getValidityHistoryTabConfig(selectedDocumentType.value));

  let isTabSwitching = false;

  // The object is kept because the tab switch moves its address: the composable reads `searchUrl`
  // on every query, not when it is created.
  const searchParams = reactive({
    search: '',
    searchType: searchType.value,
    sort: {},
    filter: buildDefaultValidityHistoryFilters(selectedDocumentType.value),
    searchUrl: getValidityHistorySearchUrl(selectedDocumentType.value),
    page: 1,
    amount: VALIDITY_HISTORY_DEFAULT_PAGE_SIZE,
    // `documentType` stays a filter on both tabs: the URL is rebuilt from the filter keys on every
    // search, so this is what keeps the tab in the address. The records endpoint drops the key.
    systemFilters: ['documentType'],
  });

  const searchComposable = useDocumentRevisionSearch(searchParams);
  const {
    items: revisions,
    metadata,
    selectedQuantity,
    allItems: allRevisions,
    loadingMore,
    hasMoreItems: hasMoreRevisions,
    shouldShowTable,
    shouldShowNoResults,
    shouldShowEmptyPage,
    handleSearch,
    handleSortOptions,
    handleFilter: baseHandleFilter,
    onPageChanged,
    handleQuantityChange,
    applyListQueryReset,
    initSearch,
    cleanup,
    filter,
    searchTerm: currentSearchTerm,
    sort,
    order,
  } = searchComposable;

  provide(SortOrderInjectionKey, { sort, order });

  const filterStore = useFilterStore();

  const handleFilter = async (filters: SearchFilter) => {
    const nextFilters = { ...filters };
    delete nextFilters.documentType;

    await baseHandleFilter({
      ...buildDefaultValidityHistoryFilters(selectedDocumentType.value),
      ...nextFilters,
      documentType: selectedDocumentType.value,
    });
  };

  const getCurrentFilters = (): SearchFilter => {
    const currentFilters = { ...filter.value };
    delete currentFilters.documentType;
    return currentFilters;
  };

  const { isFilterActive, openFilterModal } = useTableFilterModal(
    FilterHistoryModal,
    filterStore,
    handleFilter,
    getCurrentFilters
  );

  const handleFilterClicked = () => {
    openFilterModal();
  };

  const selectedTabId = computed(() => documentTypeToTabOptionId(selectedDocumentType.value));

  const handleOptionSelect = async (optionId: string) => {
    if (!isDocumentRegistrationTabOptionId(optionId) || isTabSwitching) {
      return;
    }

    const nextDocumentType = tabOptionIdToDocumentType(optionId);
    if (nextDocumentType === selectedDocumentType.value) {
      return;
    }

    isTabSwitching = true;
    selectedDocumentType.value = nextDocumentType;
    searchParams.searchUrl = getValidityHistorySearchUrl(nextDocumentType);

    try {
      // Written before the reset so the address carries the tab even when the reset fails.
      await router.replace({ query: { ...route.query, documentType: nextDocumentType } });
      filterStore.clearFilters();
      await applyListQueryReset({
        search: '',
        sort: { sort: '', order: HISTORY_DEFAULT_ORDER_OPTION.order },
        filter: buildDefaultValidityHistoryFilters(nextDocumentType),
        quantity: VALIDITY_HISTORY_DEFAULT_PAGE_SIZE,
      });
    } catch (error) {
      console.error('Erro ao aplicar filtro:', error);
    } finally {
      isTabSwitching = false;
    }
  };

  // Applied in the table slot, because the paginator hands either the page or the whole list.
  const toHistoryRows = (items: unknown[]): ValidityHistoryItem[] =>
    (items as ValidityHistoryItem[]).map((row) => ({
      ...row,
      prefix: tabConfig.value.formatPrefix(row.prefix),
    }));

  const { handleExportToExcel, isExporting } = useValidityHistoryExport({
    searchTerm: searchComposable.searchTerm,
    sort: searchComposable.sort,
    order: searchComposable.order,
    filter: searchComposable.filter,
    exportUrl: computed(() => tabConfig.value.exportUrl),
    metadata,
  });

  const isExcelExportDisabled = computed(
    () => isExporting.value || shouldShowNoResults.value || revisions.value.length === 0
  );

  const handleDelete = (_id: number) => undefined;

  onMounted(async () => {
    try {
      useFilterStore().clearFilters();
      await initSearch();
    } catch (error) {
      console.error('Erro ao realizar busca inicial:', error);
    }
  });

  onUnmounted(() => {
    resetSelection();
    documentStore.clearCache();
    cleanup();
  });

  onBeforeRouteLeave(() => {
    resetSelection();
    useFilterStore().clearFilters();
  });
</script>

<template>
  <div class="pt-4 p-6">
    <h3 class="text-gray-07 text-xl/7.5 font-semibold text-left mb-6">
      Histórico de Vigências
    </h3>

    <div class="flex flex-col items-start gap-6 mb-6">
      <TabsMenu
        :options="OPTIONS"
        :selected-option="selectedTabId"
        @option-selected="handleOptionSelect"
      />

      <ColoredButton
        :disabled="isExcelExportDisabled"
        text="Exportar para Excel"
        :icon="ExportExcelIcon"
        icon-alt="Ícone de exportação"
        @click="handleExportToExcel"
      />

      <SearchToolbar>
        <template #search>
          <FieldSearchBar
            :search-fields="SEARCH_TYPE_FIELD_OPTIONS"
            :initial-search-term="currentSearchTerm"
            class="w-full"
            @search="handleSearch"
          />
        </template>
        <template
          v-if="shouldShowTable"
          #quantity
        >
          <SelectInput
            v-model="selectedQuantity"
            :options="tabConfig.quantityOptions"
            class="cursor-pointer"
            @update:model-value="handleQuantityChange"
          />
        </template>
      </SearchToolbar>
    </div>

    <PaginateOrScroll
      v-if="shouldShowTable"
      :items="revisions"
      :all-items="allRevisions"
      :has-more-items="hasMoreRevisions"
      :loading-more="loadingMore"
      :metadata="metadata"
      :selected-quantity="selectedQuantity"
      :entity-name="tabConfig.entityName"
      @page-changed="onPageChanged"
    >
      <template #table="{ items }">
        <HistoryTable
          :revisions="toHistoryRows(items)"
          :page="metadata?.page ?? 1"
          :is-filter-active="isFilterActive"
          :order-options="HISTORY_ORDER_OPTIONS"
          @update:sort="handleSortOptions"
          @update:filter="handleFilter"
          @filter-clicked="handleFilterClicked"
          @delete="handleDelete"
        />
      </template>
    </PaginateOrScroll>

    <NoDocuments
      v-if="shouldShowEmptyPage"
      :title="tabConfig.emptyTitle"
    />
  </div>
</template>
